import { useQuery } from '@tanstack/react-query';
import './AddExistingLessonsDialog.scss';
import {
  Input,
  LoadingPlaceholder,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@trackman/web-shared-components';
import CheckboxWithoutLabel from '@trackman/web-shared-components/components/Checkbox/CheckboxWithoutLabel';
import { useServices } from 'index';
import { useState } from 'react';
import { ExistingAdminLesson } from 'types/admin/modules';

type Props = {
  open: boolean;
  onClose: (lessons: ExistingAdminLesson[] | undefined) => void;
};

export const AddExistingLessonsDialog = ({ open, onClose }: Props) => {
  const [filter, setFilter] = useState<string>('');
  const service = useServices().existingLessonsService;
  const { data, isFetching } = useQuery({ queryKey: ['existingLessons'], queryFn: service.getExistingLessons, staleTime: Infinity });

  const filteredExistingLessons = !filter ? data : data?.filter((lesson) => lesson.title.toLowerCase().includes(filter.toLowerCase()));

  const [selectedLessons, setSelectedLessons] = useState<ExistingAdminLesson[] | undefined>(undefined);
  const buttonLabel: string =
    !selectedLessons || selectedLessons.length === 0
      ? 'Add'
      : selectedLessons.length === 1
      ? 'Add 1 Lesson'
      : `Add ${selectedLessons.length} Lessons`;

  const handleClose = () => {
    onClose(undefined);
  };
  const handleAddLessons = () => {
    onClose(selectedLessons);
    setSelectedLessons(undefined);
  };
  const handleLessonToggle = (lesson: ExistingAdminLesson) => {
    if (!selectedLessons) {
      setSelectedLessons([lesson]);
    } else if (selectedLessons?.find((l) => l.id === lesson.id)) {
      setSelectedLessons(selectedLessons.filter((l) => l.id !== lesson.id));
    } else {
      setSelectedLessons([...selectedLessons, lesson]);
    }
  };

  return (
    <Modal
      className='existing-lessons-modal'
      cancelBtn={{
        label: 'Cancel',
        onClick: handleClose,
      }}
      confirmBtn={{
        label: buttonLabel,
        onClick: handleAddLessons,
        disabled: !selectedLessons || selectedLessons?.length === 0,
      }}
      onClose={handleClose}
      title={'Add existing lessons'}
      open={open}
    >
      {!isFetching ? (
        <div className='existing-lessons-content'>
          <Input
            label='Search Chapter Library'
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 20 }}></TableCell>
                  <TableCell className='existing-lesson-name-header'>Lesson Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExistingLessons
                  ?.sort((a, b) => a.title.localeCompare(b.title))
                  ?.map((lesson, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <CheckboxWithoutLabel
                          onChange={() => handleLessonToggle(lesson)}
                          checked={!!selectedLessons?.find((l) => l.id === lesson.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className='existing-lesson-cell'>
                          <span>{lesson.title.toLowerCase()}</span>
                          <span>{lesson.kind}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <LoadingPlaceholder />
      )}
    </Modal>
  );
};
